import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Input, Button } from '@heroui/react';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter } from '@heroui/drawer';
import { addToast } from '@heroui/toast';
import type { Venue, CreateVenueRequest, VenueVisibility } from '@/types/venue';
import { createVenue, updateVenue } from '@/api/venueApi';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { IconChevronLeft as ChevronLeft, IconWorld as Globe, IconLock as Lock, IconMapPin as MapPin, IconX as X } from '@tabler/icons-react';
import useIsMobile from '../hooks/useIsMobile';
import OrganizerAccessPicker from '@/components/venue/OrganizerAccessPicker';
import { btnAccent, label as labelCls } from '@/components/ui/design';

const EMPTY_FORM: CreateVenueRequest = {
    name: '',
    address: { fullAddress: '', country: '', state: '', city: '', zipcode: '' },
    lat: '',
    lng: '',
    seatMap: null,
    visibility: 'selected',
    allowedOrganizerIds: [],
};

const VISIBILITY_OPTIONS: { value: VenueVisibility; label: string; hint: string; icon: typeof Lock }[] = [
    { value: 'selected', label: 'Selected organizers', hint: 'Only organizers you choose can find and use this venue. Only your organization\'s owner, admins and event managers can edit it', icon: Lock },
    { value: 'all', label: 'All organizers', hint: 'Any organizer can find and use this venue. Only your organization\'s owner, admins and event managers can edit it', icon: Globe },
];

const DEFAULT_CENTER = { lat: 40.7128, lng: -74.0060 };
const MAP_LIBRARIES: ('places')[] = ['places'];
const PLACE_FIELDS = ['geometry', 'formatted_address', 'address_components', 'name'];
const mapContainerStyle = { width: '100%', height: '228px', borderRadius: '20px' };

type AddressParts = { country: string; state: string; city: string; zipcode: string };

const CITY_TYPES = ['locality', 'postal_town', 'sublocality_level_1', 'sublocality', 'administrative_area_level_3', 'administrative_area_level_2', 'neighborhood'];

function pickComponent(addressComponents: any[], types: string[], useShortName = false): string {
    for (const type of types) {
        const component = addressComponents.find(c => c.types?.includes(type));
        const value = useShortName ? component?.short_name : component?.long_name;
        if (value) return value;
    }
    return '';
}

function extractAddressFromComponents(addressComponents: any[]): AddressParts {
    if (!Array.isArray(addressComponents)) return { country: '', state: '', city: '', zipcode: '' };
    return {
        country: pickComponent(addressComponents, ['country']),
        state: pickComponent(addressComponents, ['administrative_area_level_1']),
        city: pickComponent(addressComponents, CITY_TYPES),
        zipcode: pickComponent(addressComponents, ['postal_code']),
    };
}

function parseFormattedAddress(formatted: string): Partial<AddressParts> {
    const parts = formatted.split(',').map(part => part.trim()).filter(Boolean);
    const result: Partial<AddressParts> = {};
    const stateZip = [...parts].reverse().map(part => part.match(/^([A-Za-z]{2,}(?:\s[A-Za-z]+)*)\s+(\d{5}(?:-\d{4})?|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/)).find(Boolean);
    if (stateZip) {
        result.state = stateZip[1];
        result.zipcode = stateZip[2];
        const index = parts.findIndex(part => part.startsWith(stateZip[0]));
        if (index > 0) result.city = parts[index - 1];
    }
    if (parts.length >= 2 && /^(USA|United States|Canada)$/i.test(parts[parts.length - 1])) {
        result.country = parts[parts.length - 1].replace(/^USA$/i, 'United States');
    }
    return result;
}

function mergeMissing(base: AddressParts, extra: Partial<AddressParts>): AddressParts {
    return {
        country: base.country || extra.country || '',
        state: base.state || extra.state || '',
        city: base.city || extra.city || '',
        zipcode: base.zipcode || extra.zipcode || '',
    };
}

export default function VenueFormModal({
    isOpen,
    venue,
    onClose,
    onSaved,
}: {
    isOpen: boolean;
    // Passed in directly from the venue list already in memory — no need to re-fetch by id.
    venue?: Venue | null;
    onClose: () => void;
    onSaved?: (venue: Venue) => void;
}) {
    const queryClient = useQueryClient();
    const isEditing = Boolean(venue);

    const [formData, setFormData] = useState<CreateVenueRequest>(EMPTY_FORM);
    const [center, setCenter] = useState(DEFAULT_CENTER);
    const [suggestions, setSuggestions] = useState<{ id: string; main: string; secondary: string; description: string }[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeSuggestion, setActiveSuggestion] = useState(-1);
    const sessionTokenRef = useRef<any>(null);
    const debounceRef = useRef<number | undefined>(undefined);
    const [map, setMap] = useState<any>(null);
    const geocoderRef = useRef<any>(null);
    const resolvedAddressRef = useRef('');
    const requestRef = useRef(0);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        libraries: MAP_LIBRARIES,
    });

    const getGeocoder = useCallback(() => {
        if (!geocoderRef.current && (window as any).google?.maps) geocoderRef.current = new (window as any).google.maps.Geocoder();
        return geocoderRef.current;
    }, []);

    const markerPosition = (() => {
        const lat = parseFloat(formData.lat || '');
        const lng = parseFloat(formData.lng || '');
        return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    })();

    // Populate the form whenever the modal is opened, for both create and edit.
    useEffect(() => {
        if (!isOpen) return;
        if (venue) {
            setFormData({
                name: venue.name,
                address: {
                    fullAddress: venue.address?.fullAddress || '',
                    country: venue.address?.country || '',
                    state: venue.address?.state || '',
                    city: venue.address?.city || '',
                    zipcode: venue.address?.zipcode || '',
                },
                lat: venue.lat || '',
                lng: venue.lng || '',
                visibility: venue.visibility ?? 'all',
                allowedOrganizerIds: venue.allowedOrganizerIds ?? [],
            });
            setCenter(venue.lat && venue.lng ? { lat: parseFloat(venue.lat), lng: parseFloat(venue.lng) } : DEFAULT_CENTER);
            resolvedAddressRef.current = venue.lat && venue.lng ? venue.address?.fullAddress || '' : '';
        } else {
            setFormData(EMPTY_FORM);
            setCenter(DEFAULT_CENTER);
            resolvedAddressRef.current = '';
        }
        requestRef.current += 1;
    }, [isOpen, venue]);

    const moveTo = useCallback((lat: number, lng: number) => {
        setCenter({ lat, lng });
        map?.panTo({ lat, lng });
        if ((map?.getZoom?.() ?? 0) < 15) map?.setZoom(15);
    }, [map]);

    const geocode = useCallback((request: any) => new Promise<any>(resolve => {
        const geocoder = getGeocoder();
        if (!geocoder) return resolve(null);
        geocoder.geocode(request, (results: any[] | null, status: string) => resolve(status === 'OK' && results?.[0] ? results[0] : null));
    }), [getGeocoder]);

    const completeAddress = useCallback(async (address: CreateVenueRequest['address'], location: { lat: number; lng: number } | null): Promise<CreateVenueRequest['address']> => {
        let parts: AddressParts = { country: address.country || '', state: address.state || '', city: address.city || '', zipcode: address.zipcode || '' };
        const isComplete = () => !!(parts.country && parts.state && parts.city && parts.zipcode);
        if (!isComplete() && location) {
            const geocoder = getGeocoder();
            const results: any[] = geocoder
                ? await new Promise<any[]>(resolve => geocoder.geocode({ location }, (list: any[] | null, status: string) => resolve(status === 'OK' && list ? list : [])))
                : [];
            for (const result of results) {
                parts = mergeMissing(parts, extractAddressFromComponents(result.address_components));
                if (isComplete()) break;
            }
        }
        if (!isComplete()) parts = mergeMissing(parts, parseFormattedAddress(address.fullAddress));
        return { ...address, ...parts };
    }, [getGeocoder]);

    const placesLookup = useCallback((method: 'textSearch' | 'nearbySearch' | 'getDetails', request: any) => new Promise<any>(resolve => {
        const maps = (window as any).google?.maps;
        if (!maps?.places) return resolve(null);
        const service = new maps.places.PlacesService(map ?? document.createElement('div'));
        service[method](request, (result: any, status: string) => {
            if (status !== maps.places.PlacesServiceStatus.OK || !result) return resolve(null);
            resolve(method === 'getDetails' ? result : result[0] ?? null);
        });
    }), [map]);

    const placeDetails = useCallback(async (hit: any) => {
        if (!hit?.place_id) return null;
        return placesLookup('getDetails', { placeId: hit.place_id, fields: PLACE_FIELDS });
    }, [placesLookup]);

    const findByText = useCallback(async (query: string) => {
        return (await geocode({ address: query })) ?? (await placeDetails(await placesLookup('textSearch', { query })));
    }, [geocode, placeDetails, placesLookup]);

    const findByLocation = useCallback(async (location: { lat: number; lng: number }) => {
        const exact = await geocode({ location });
        if (exact) return exact;
        const rankBy = (window as any).google?.maps?.places?.RankBy?.DISTANCE;
        const attempts = [
            ...(rankBy ? [{ location, rankBy, type: 'establishment' }] : []),
            { location, radius: 150 },
        ];
        for (const request of attempts) {
            const details = await placeDetails(await placesLookup('nearbySearch', request));
            if (details?.formatted_address) return details;
        }
        return null;
    }, [geocode, placeDetails, placesLookup]);

    const geocodeText = useCallback(async (text: string) => {
        const query = text.trim();
        if (!query || query === resolvedAddressRef.current) return;
        const request = ++requestRef.current;
        const result = await findByText(query);
        if (request !== requestRef.current) return;
        const location = result?.geometry?.location;
        if (!result || !location) {
            addToast({ title: 'Address not found', description: 'Pick a suggestion from the list or place the pin on the map.', color: 'warning' });
            return;
        }
        const formatted = result.formatted_address || query;
        resolvedAddressRef.current = formatted;
        setFormData(prev => ({
            ...prev,
            address: { ...prev.address, ...extractAddressFromComponents(result.address_components), fullAddress: formatted },
            lat: location.lat().toString(),
            lng: location.lng().toString(),
        }));
        moveTo(location.lat(), location.lng());
    }, [findByText, moveTo]);

    const placeAt = useCallback(async (lat: number, lng: number) => {
        setFormData(prev => ({ ...prev, lat: lat.toString(), lng: lng.toString() }));
        setCenter({ lat, lng });
        const request = ++requestRef.current;
        const result = await findByLocation({ lat, lng });
        if (request !== requestRef.current) return;
        if (!result?.formatted_address) {
            addToast({ title: 'No address found here', description: 'The pin was moved. You can type the address in the search box.', color: 'warning' });
            return;
        }
        resolvedAddressRef.current = result.formatted_address;
        setFormData(prev => ({
            ...prev,
            address: { ...prev.address, ...extractAddressFromComponents(result.address_components), fullAddress: result.formatted_address },
        }));
    }, [findByLocation]);

    useEffect(() => () => window.clearTimeout(debounceRef.current), []);

    useEffect(() => {
        if (!isOpen || !isLoaded || !map || !venue || (venue.lat && venue.lng) || !venue.address?.fullAddress) return;
        geocodeText(venue.address.fullAddress);
    }, [isOpen, isLoaded, map, venue, geocodeText]);

    const saveMutation = useMutation({
        mutationFn: ({ seatMap: _seatMap, ...data }: CreateVenueRequest) =>
            isEditing ? updateVenue(venue!._id, data) : createVenue(data),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['venues'] });
            queryClient.invalidateQueries({ queryKey: ['venue-picker'] });
            queryClient.invalidateQueries({ queryKey: ['venue-browser'] });
            if (isEditing) queryClient.invalidateQueries({ queryKey: ['venue', venue!._id] });
            addToast({
                title: 'Success',
                description: isEditing ? 'Venue updated successfully!' : 'Venue created successfully!',
                color: 'success',
            });
            if (res?.data) onSaved?.(res.data);
            onClose();
        },
        onError: (err: any) => {
            addToast({
                title: 'Error',
                description: err?.response?.data?.message || err?.message || 'Failed to save venue. Please try again.',
                color: 'danger',
            });
        },
    });

    function handleAddressField(field: keyof CreateVenueRequest['address'], value: string) {
        setFormData(prev => ({ ...prev, address: { ...prev.address, [field]: value } }));
    }

    function applyPlace(place: any, fallbackText: string) {
        const location = place?.geometry?.location;
        if (!location) return false;
        const fullAddress = place.formatted_address || place.name || fallbackText;
        requestRef.current += 1;
        resolvedAddressRef.current = fullAddress;
        setFormData(prev => ({
            ...prev,
            address: { fullAddress, ...extractAddressFromComponents(place.address_components) },
            lat: location.lat().toString(),
            lng: location.lng().toString(),
        }));
        moveTo(location.lat(), location.lng());
        return true;
    }

    function closeSuggestions() {
        window.clearTimeout(debounceRef.current);
        setShowSuggestions(false);
        setSuggestions([]);
        setActiveSuggestion(-1);
    }

    function handleAddressInput(value: string) {
        handleAddressField('fullAddress', value);
        setActiveSuggestion(-1);
        window.clearTimeout(debounceRef.current);
        const maps = (window as any).google?.maps;
        if (value.trim().length < 3 || !maps?.places) {
            setSuggestions([]);
            return;
        }
        debounceRef.current = window.setTimeout(() => {
            sessionTokenRef.current = sessionTokenRef.current ?? new maps.places.AutocompleteSessionToken();
            new maps.places.AutocompleteService().getPlacePredictions(
                { input: value, componentRestrictions: { country: ['us', 'ca'] }, sessionToken: sessionTokenRef.current },
                (predictions: any[] | null, status: string) => {
                    const list = status === 'OK' && predictions ? predictions : [];
                    setSuggestions(list.map(p => ({
                        id: p.place_id,
                        main: p.structured_formatting?.main_text || p.description,
                        secondary: p.structured_formatting?.secondary_text || '',
                        description: p.description,
                    })));
                    setShowSuggestions(true);
                },
            );
        }, 220);
    }

    async function pickSuggestion(suggestion: { id: string; description: string }) {
        const token = sessionTokenRef.current;
        sessionTokenRef.current = null;
        closeSuggestions();
        handleAddressField('fullAddress', suggestion.description);
        const request = ++requestRef.current;
        const place = await placesLookup('getDetails', { placeId: suggestion.id, fields: PLACE_FIELDS, sessionToken: token });
        if (request !== requestRef.current) return;
        if (!applyPlace(place, suggestion.description)) {
            addToast({ title: 'Could not load that place', description: 'Try another suggestion or place the pin on the map.', color: 'warning' });
        }
    }

    function handleAddressKeyDown(e: React.KeyboardEvent) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (!suggestions.length) return;
            e.preventDefault();
            setShowSuggestions(true);
            setActiveSuggestion(prev => (e.key === 'ArrowDown' ? (prev + 1) % suggestions.length : (prev - 1 + suggestions.length) % suggestions.length));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (showSuggestions && activeSuggestion >= 0 && suggestions[activeSuggestion]) pickSuggestion(suggestions[activeSuggestion]);
            else {
                closeSuggestions();
                geocodeText(formData.address.fullAddress);
            }
        } else if (e.key === 'Escape' && showSuggestions) {
            e.stopPropagation();
            closeSuggestions();
        }
    }

    const isMobile = useIsMobile();

    async function handleSave() {
        if (!formData.name.trim()) {
            addToast({ title: 'Venue name required', description: 'Enter a name for this venue.', color: 'warning' });
            return;
        }
        if (!formData.address.fullAddress.trim() || !markerPosition) {
            addToast({ title: 'Location required', description: 'Search for an address and pick a suggestion, or click the map to place the pin.', color: 'warning' });
            return;
        }
        const address = await completeAddress(formData.address, markerPosition);
        if (!address.city || !address.zipcode || !address.state || !address.country) {
            addToast({ title: 'Address incomplete', description: 'We could not find the city, state or zip code for this location. Pick a more specific address or move the pin.', color: 'warning' });
            return;
        }
        setFormData(prev => ({ ...prev, address }));
        saveMutation.mutate({ ...formData, address });
    }

    const saveButton = (
        <Button
            onPress={handleSave}
            isLoading={saveMutation.isPending}
            className={`${btnAccent} h-11 px-5`}
        >
            Save Venue
        </Button>
    );

    const inputClassNames = {
        inputWrapper: 'h-12 rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
        input: 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
        label: 'hidden',
    };
    const labelClass = labelCls;

    return (
        <Drawer
            placement="right"
            hideCloseButton
            isOpen={isOpen}
            onClose={onClose}
            size={isMobile ? 'full' : 'lg'}
            classNames={{
                wrapper: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                base: isMobile ? 'bg-white dark:bg-nightSurface h-[100dvh] my-0 z-[100000]' : 'bg-white dark:bg-nightSurface h-[calc(100dvh-80px)] my-0 z-[100000]',
                backdrop: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                header: 'border-b border-black/[0.06] dark:border-white/10 pb-4',
                body: 'py-6 flex-1',
                footer: 'border-t border-black/[0.06] dark:border-white/10 pt-4',
            }}
            scrollBehavior="inside"
        >
            <DrawerContent>
                {isMobile ? (
                    <div className="flex items-center justify-between px-4 py-3 border-b border-black/[0.06] dark:border-white/10">
                        <button onClick={onClose} className="flex h-10 items-center gap-1.5 rounded-full pl-2 pr-4 text-sm font-semibold text-grey10 ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]">
                            <ChevronLeft className="w-4 h-4" />
                            Back
                        </button>
                        {saveButton}
                    </div>
                ) : (
                    <DrawerHeader className="flex items-center justify-between px-4 md:px-6">
                        <div className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">{isEditing ? 'Edit Venue' : 'New Venue'}</div>
                        <button onClick={onClose} aria-label="Close" className="w-10 h-10 rounded-full ring-1 ring-grey90 dark:ring-white/15 flex items-center justify-center text-grey10 hover:bg-grey99 dark:text-white dark:hover:bg-white/[0.06]">
                            <X className="w-4 h-4" />
                        </button>
                    </DrawerHeader>
                )}

                <DrawerBody className="flex flex-col gap-6 px-4 md:px-6">
                    {isMobile && <div className="text-[clamp(28px,8vw,36px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">{isEditing ? 'Edit Venue' : 'New Venue'}</div>}
                    <div>
                        <label className={labelClass}>Venue Name</label>
                        <Input aria-label="Venue name" placeholder="Venue Name..." classNames={inputClassNames} value={formData.name} onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))} isRequired />
                    </div>

                    <div>
                        <label className={labelClass}>Visibility</label>
                        <div role="radiogroup" aria-label="Visibility" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {VISIBILITY_OPTIONS.map(({ value, label, hint, icon: Icon }) => {
                                const active = formData.visibility === value;
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        onClick={() => setFormData(prev => ({ ...prev, visibility: value }))}
                                        className={`flex items-start gap-3 p-4 rounded-2xl text-left transition-all ${active ? 'bg-primary99 ring-2 ring-primary dark:bg-nightAccent/10 dark:ring-nightAccent' : 'bg-grey99 ring-1 ring-black/[0.04] hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10'}`}
                                    >
                                        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${active ? 'text-primary dark:text-nightAccent' : 'text-grey50 dark:text-nightMuted'}`} />
                                        <span>
                                            <span className="block text-sm font-semibold text-grey10 dark:text-white">{label}</span>
                                            <span className="mt-0.5 block text-xs text-grey40 dark:text-nightMuted">{hint}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        {formData.visibility === 'selected' && (
                            <OrganizerAccessPicker
                                value={formData.allowedOrganizerIds ?? []}
                                onChange={next => setFormData(prev => ({ ...prev, allowedOrganizerIds: next }))}
                            />
                        )}
                    </div>

                    <div className="flex flex-col gap-4">
                        <div>
                            <label className={labelClass}>Location</label>
                            {isLoaded ? (
                                <div className="relative">
                                    <Input
                                        aria-label="Location"
                                        placeholder="Search for a place or address..."
                                        classNames={inputClassNames}
                                        value={formData.address.fullAddress}
                                        onChange={e => handleAddressInput(e.target.value)}
                                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                        onBlur={() => {
                                            window.setTimeout(() => setShowSuggestions(false), 120);
                                            geocodeText(formData.address.fullAddress);
                                        }}
                                        onKeyDown={handleAddressKeyDown}
                                        autoComplete="off"
                                        role="combobox"
                                        aria-expanded={showSuggestions && suggestions.length > 0}
                                        aria-autocomplete="list"
                                        isRequired
                                    />
                                    {showSuggestions && suggestions.length > 0 && (
                                        <ul role="listbox" aria-label="Address suggestions" className="absolute left-0 right-0 top-full z-20 mt-2 max-h-64 overflow-y-auto rounded-2xl bg-white p-1.5 shadow-xl ring-1 ring-black/[0.06] dark:bg-nightElevated dark:ring-white/10">
                                            {suggestions.map((s, i) => (
                                                <li
                                                    key={s.id}
                                                    role="option"
                                                    aria-selected={i === activeSuggestion}
                                                    onMouseDown={e => e.preventDefault()}
                                                    onClick={() => pickSuggestion(s)}
                                                    onMouseEnter={() => setActiveSuggestion(i)}
                                                    className={`flex cursor-pointer items-start gap-2 rounded-xl px-3 py-2.5 text-sm ${i === activeSuggestion ? 'bg-grey99 dark:bg-white/10' : ''}`}
                                                >
                                                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-grey50 dark:text-nightMuted" />
                                                    <span className="min-w-0">
                                                        <span className="block truncate font-semibold text-grey10 dark:text-white">{s.main}</span>
                                                        {s.secondary && <span className="block truncate text-xs text-grey40 dark:text-nightMuted">{s.secondary}</span>}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ) : (
                                <div className="flex h-12 items-center rounded-2xl bg-grey99 px-4 text-sm text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Loading map...</div>
                            )}
                        </div>

                        {isLoaded && (
                            <div className="rounded-[28px] bg-grey99 p-2 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    zoom={markerPosition ? 15 : 12}
                                    onLoad={setMap}
                                    center={center}
                                    onClick={e => { (e as any).stop?.(); if (e.latLng) placeAt(e.latLng.lat(), e.latLng.lng()); }}
                                    options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: 'cooperative' }}
                                >
                                    {markerPosition && (
                                        <Marker
                                            position={markerPosition}
                                            draggable
                                            onDragEnd={e => { if (e.latLng) placeAt(e.latLng.lat(), e.latLng.lng()); }}
                                        />
                                    )}
                                </GoogleMap>
                                <p className="px-2 pt-2 pb-1 text-xs text-grey40 dark:text-nightMuted">
                                    {markerPosition ? 'Drag the pin or click the map to fine-tune the location.' : 'Search an address above, or click the map to place the pin.'}
                                </p>
                            </div>
                        )}
                    </div>
                </DrawerBody>

                {!isMobile && (
                    <DrawerFooter className="flex items-center justify-end gap-2 px-4 md:px-6">
                        <button onClick={onClose} disabled={saveMutation.isPending} className="inline-flex h-11 items-center justify-center rounded-full px-5 text-sm font-semibold text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 disabled:opacity-60 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]">Cancel</button>
                        {saveButton}
                    </DrawerFooter>
                )}
            </DrawerContent>
        </Drawer>
    );
}
